import React, { useEffect, useState } from "react";
import HorziBarChartsMain from "./HorizontalStackBarChart";
import CustomPieChart from "../components/CustomPieChart";
import BarChartsMain from "./BarChartsMain";
import {
  ageRangeWiseCountApi,
  emiWiseCountApi,
  genderWiseCountApi,
  salaryRangeWiseCountApi,
  stateWiseCountApi,
} from "../../../maketingComponents/actioncreator";
import moment from "moment";
import Loader from "../../../../../presentationals/Loader/Loader.component";

const isWideScreen = window.innerWidth > 1800;
const fs = (base) => `${isWideScreen ? Math.round(base * 1.25) : base}px`;

// ─── Mini inline-bar for table cells ────────────────────────────────────────
const InlineBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          flex: 1,
          height: "6px",
          background: "#f0f0f0",
          borderRadius: "99px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: "99px",
            transition: "width 0.6s ease",
          }}
        />
      </div>
      <span style={{ fontSize: fs(12), color: "#555", minWidth: "28px", textAlign: "right" }}>
        {value}
      </span>
    </div>
  );
};

// ─── Reusable table wrapper ──────────────────────────────────────────────────
const SectionTable = ({ title, columns, rows, accentColor }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      height:"100%"
    }}
  >
    {/* Header strip */}
    <div
      style={{
        padding: "14px 20px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: "#fafafa",
      }}
    >
      <span
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: accentColor,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span style={{ fontWeight: 700, fontSize: fs(14), color: "#111" }}>{title}</span>
      <span
        style={{
          marginLeft: "auto",
          background: accentColor + "22",
          color: accentColor,
          fontSize: fs(11),
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: "99px",
        }}
      >
        {rows.length} entries
      </span>
    </div>

    {/* Table */}
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(13) }}>
        <thead>
          <tr style={{ background: "#f8f9fa" }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  padding: "10px 20px",
                  textAlign: col.align || "left",
                  fontWeight: 600,
                  color: "#6b7280",
                  fontSize: fs(11),
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderBottom: "1px solid #e5e7eb",
                  whiteSpace: "nowrap",
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}
              >
                No data available
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: "1px solid #f3f4f6",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      padding: "10px 20px",
                      textAlign: col.align || "left",
                      color: "#374151",
                      verticalAlign: "middle",
                    }}
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key] ?? "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Table view ──────────────────────────────────────────────────────────────
const TableView = ({ stateWiseData, genderWiseData, ageWiseData, salaryWiseData, emiWiseData }) => {
  const maxState = Math.max(...(stateWiseData.map((d) => d.total || 0)), 1);
  const maxAge = Math.max(...(ageWiseData.map((d) => d.count || 0)), 1);
  const maxSalary = Math.max(...(salaryWiseData.map((d) => d.count || 0)), 1);
  const maxEmi = Math.max(...(emiWiseData.map((d) => d.count || 0)), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "20px" }}>

      {/* State / Daily Trend */}
      <SectionTable
        title="State-wise Lead Trend"
        accentColor="#4FA7C7"
        columns={[
          { key: "state", label: "State" },
          {
            key: "total",
            label: "Total Leads",
            render: (v) => <InlineBar value={v || 0} max={maxState} color="#4FA7C7" />,
          },
          {
            key: "count",
            label: "Conversions",
            render: (v) => <InlineBar value={v || 0} max={maxState} color="#4FA564" />,
          },
          {
            key: "_conv",
            label: "Conv. Rate",
            align: "right",
            render: (_, row) => {
              const rate =
                row.total > 0 ? ((row.count / row.total) * 100).toFixed(1) : "0.0";
              const color = rate >= 10 ? "#22c55e" : rate >= 5 ? "#f59e0b" : "#ef4444";
              return (
                <span
                  style={{
                    background: color + "18",
                    color,
                    padding: "2px 10px",
                    borderRadius: "99px",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  {rate}%
                </span>
              );
            },
          },
        ]}
        rows={stateWiseData}
      />

      {/* Gender + Age side by side */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 300px" }}>
          <SectionTable
            title="Gender Distribution"
            accentColor="#035865"
            columns={[
              { key: "name", label: "Gender" },
              {
                key: "value",
                label: "Count",
                render: (v, row) => (
                  <InlineBar value={v || 0} max={Math.max(...genderWiseData.map((d) => d.value || 0), 1)} color={row.color || "#035865"} />
                ),
              },
              {
                key: "_pct",
                label: "Share",
                align: "right",
                render: (_, row) => {
                  const total = genderWiseData.reduce((s, d) => s + (d.value || 0), 0);
                  const pct = total > 0 ? ((row.value / total) * 100).toFixed(1) : "0.0";
                  return (
                    <span style={{ fontWeight: 600, color: row.color || "#374151" }}>
                      {pct}%
                    </span>
                  );
                },
              },
            ]}
            rows={genderWiseData}
          />
        </div>

        <div style={{ flex: "1 1 300px" }}>
          <SectionTable
            title="Age Distribution"
            accentColor="#EBB569"
            columns={[
              { key: "emiType", label: "Age Range" },
              {
                key: "count",
                label: "Count",
                render: (v) => <InlineBar value={v || 0} max={maxAge} color="#EBB569" />,
              },
            ]}
            rows={ageWiseData}
          />
        </div>
      </div>

      {/* Salary + EMI side by side */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 300px" }}>
          <SectionTable
            title="Salary Range"
            accentColor="#7184FF"
            columns={[
              { key: "range", label: "Salary Range" },
              {
                key: "count",
                label: "Count",
                render: (v) => <InlineBar value={v || 0} max={maxSalary} color="#7184FFBA" />,
              },
            ]}
            rows={salaryWiseData}
          />
        </div>

        <div style={{ flex: "1 1 300px" }}>
          <SectionTable
            title="EMI Duration Distribution"
            accentColor="#FF98C5"
            columns={[
              { key: "emiType", label: "EMI Type" },
              {
                key: "count",
                label: "Count",
                render: (v) => <InlineBar value={v || 0} max={maxEmi} color="#FF98C5B0" />,
              },
            ]}
            rows={emiWiseData}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Chart view (original layout) ────────────────────────────────────────────
const ChartView = ({ stateWiseData, genderWiseData, ageWiseData, salaryWiseData, emiWiseData }) => (
  <>
    <div style={{ display: "flex", gap: "20px", marginTop: "25px", flexWrap: "wrap" }}>
      <div style={{ flex: "2" }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #00000026",
            borderRadius: "10px",
            padding: "20px",
            height: "100%",
          }}
        >
          <HorziBarChartsMain
            customGraphOption={true}
            heading={"Daily Trend"}
            xAxisDataKey={"state"}
            barData={stateWiseData}
            dataKeyName1={"total"}
            dataKeyName2={"count"}
            barcolor1={"#4FA7C7"}
            barcolor2={"#4FA564"}
            LegendLeft={"Total Leads"}
            LegendRight={"Conversion"}
            style={{ fontSize: "16px", fontWeight: 600, marginTop: "0px", marginBottom: "14px" }}
          />
        </div>
      </div>
    </div>

    <div
      style={{
        display: "flex",
        gap: window.innerWidth > 1800 ? "40px" : "20px",
        marginTop: "50px",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 30%" }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #00000026",
            borderRadius: "10px",
            padding: "20px",
            height: "100%",
          }}
        >
          <CustomPieChart title="Gender Distribution" data={genderWiseData} />
        </div>
      </div>
      <div
        style={{
          flex: "1 1 30%",
          background: "#fff",
          border: "1px solid #00000026",
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        <BarChartsMain
          barcolor={"#EBB569"}
          xAxisDataKey={"emiType"}
          barData={ageWiseData}
          yAxisLabel={"count"}
          heading={"Age Distribution"}
          className={"common-heading"}
        />
      </div>
    </div>

    <div
      style={{
        display: "flex",
        gap: window.innerWidth > 1800 ? "40px" : "20px",
        marginTop: "50px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          flex: "1 1 30%",
          background: "#fff",
          border: "1px solid #00000026",
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        <BarChartsMain
          barcolor={"#7184FFBA"}
          xAxisDataKey={"range"}
          barData={salaryWiseData}
          yAxisLabel={"count"}
          heading={"Salary Range"}
          className={"common-heading"}
        />
      </div>
      <div
        style={{
          flex: "1 1 30%",
          background: "#fff",
          border: "1px solid #00000026",
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        <BarChartsMain
          barcolor={"#FF98C5B0"}
          xAxisDataKey={"emiType"}
          barData={emiWiseData}
          yAxisLabel={"count"}
          heading={"EMI Duration Distribution"}
          className={"common-heading"}
        />
      </div>
    </div>
  </>
);

// ─── Main Component ───────────────────────────────────────────────────────────
function LeadDemographicProfile({ appliedDate, view }) {
  const [isLoading, setIsLoading] = useState(false);
  const [stateWiseData, setStateWiseData] = useState([]);
  const [genderWiseData, setGenderWiseData] = useState([]);
  const [ageWiseData, setAgeWiseData] = useState([]);
  const [salaryWiseData, setSalaryWiseData] = useState([]);
  const [emiWiseData, setEmiWiseData] = useState([]);

  const buildPayload = () => ({
    fromDate: appliedDate?.from ? moment(appliedDate.from).format("DD-MM-YYYY") : "",
    toDate: appliedDate?.to ? moment(appliedDate.to).format("DD-MM-YYYY") : "",
  });

  const getStateWiseData = () => {
    setIsLoading(true);
    stateWiseCountApi(
      buildPayload(), "",
      (res) => { setIsLoading(false); setStateWiseData(res); },
      (err) => { setIsLoading(false); console.log("State wise data error", err); },
    );
  };

  const getGenderWiseData = () => {
    setIsLoading(true);
    genderWiseCountApi(
      buildPayload(),
      (res) => {
        setIsLoading(false);
        setGenderWiseData(res.map((item) => ({
          name: item?.gender,
          value: item?.count,
          color: item?.gender === "Male" ? "#035865" : item.gender === "Female" ? "#EB6062" : "#6B7280",
        })));
      },
      (err) => { setIsLoading(false); console.log("Gender wise Error", err); },
    );
  };

  const getAgeWiseData = () => {
    setIsLoading(true);
    ageRangeWiseCountApi(
      buildPayload(),
      (res) => { setIsLoading(false); setAgeWiseData(res); },
      (err) => { setIsLoading(false); console.log("Age wise Error", err); },
    );
  };

  const getSalaryWiseData = () => {
    setIsLoading(true);
    salaryRangeWiseCountApi(
      buildPayload(),
      (res) => { setIsLoading(false); setSalaryWiseData(res); },
      (err) => { setIsLoading(false); console.log("Salary wise Error", err); },
    );
  };

  const getEmiWiseData = () => {
    setIsLoading(true);
    emiWiseCountApi(
      buildPayload(),
      (res) => { setIsLoading(false); setEmiWiseData(res); },
      (err) => { setIsLoading(false); console.log("EMI wise Error", err); },
    );
  };

  useEffect(() => {
    getStateWiseData();
    getGenderWiseData();
    getAgeWiseData();
    getSalaryWiseData();
    getEmiWiseData();
  }, [appliedDate]);

  const sharedProps = { stateWiseData, genderWiseData, ageWiseData, salaryWiseData, emiWiseData };

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : view === "table" ? (
        <TableView {...sharedProps} />
      ) : (
        <ChartView {...sharedProps} />
      )}
    </div>
  );
}

export default LeadDemographicProfile;